/**
 * Danger-tile event mechanics (HEX-81 origin; reframed for chance events in
 * HEX-137 / ADR-0023).
 *
 * `dangerous` tiles store no tier (ADR-0012: they carry a `null` lifecycle).
 * Their bite is derived from coords instead: the safe-zone distance picks a
 * tier band — the same outward gradient enemies use (`tierBands`) — and the
 * band scales the event (cache payout, ambush context). Pure + deterministic so
 * the `EventFlow` modal and its tests share one source of truth.
 */

import { safeZoneDistance } from './frontierRisk';
import { tierBand } from './tierBands';

/**
 * Numeric danger tier for a tile, 1-based (1..5). Band 0 (the spawn
 * neighbourhood) → tier 1; the deep frontier (band 4) → tier 5. 1-based so a
 * deeper-frontier event always reads as at least tier 1 — there are no zero-tier
 * tiles even next to spawn.
 */
export function dangerTierAt(q: number, r: number, seed?: number): number {
  // `seed` folds frontier settlements into the safe-zone metric (HEX-93), so an
  // event beside a frontier town reads at a lower tier than one in open frontier
  // at the same origin distance. Optional + additive — omit for origin-only.
  return tierBand(safeZoneDistance(q, r, seed)) + 1;
}

/**
 * Deterministic seed for a danger tile's chance event — mixes coords + footfall
 * so a re-cross re-rolls, but a given `(tile, crossing)` is replayable. Feeds
 * `createRng` (src/combat/dice.ts), the standalone die-roll RNG. (Was the trap
 * escape-roll seed pre-HEX-137; the nonce pattern is unchanged.)
 */
export function eventSeed(q: number, r: number, crossings: number): number {
  return (Math.imul(q, 73856093) ^ Math.imul(r, 19349663) ^ Math.imul(crossings, 83492791)) >>> 0;
}
